import { config } from './config.js';
import { getGenotypeDisplayParts } from 'src/util/util';
import { sortLabelValues } from './labelValueSort';
import {
  getCarrierStatusLabel,
  isUnrecognisedCarrierStatus
} from 'src/components/Constants';

// Shared, d3-FREE label-wrapping helpers.
//
// Long node labels (Tests / Diagnosis / Consultation Date / Test Comment) used
// to render as one comma-joined horizontal line and overflow into neighbouring
// nodes. They now render as a HEADER line followed by one item per line, each
// item word-wrapped to the space available between nodes.
//
// Wrapping is a CHAR estimate (not pixel measurement) on purpose: the same pure
// function is used by getLabelPos (to reserve the exact vertical space) AND by
// the Canvas renderer (to draw the lines), so the two can never disagree on how
// many lines a block produces — which is what guarantees stacked labels never
// overlap. A conservative average glyph width keeps lines inside the budget so
// they don't overflow horizontally either. (The pixel-exact d3 `wrap()` can't be
// used for reservation because getLabelPos runs before anything is in the DOM.)

// Conservative average glyph width as a fraction of font size. Slightly high so
// estimated lines are a touch SHORTER than the pixel budget → never overflow.
const AVG_GLYPH_W = 0.6;

// Vertical pitch between stacked label rows, as a multiple of font size.
//
// SINGLE SOURCE OF TRUTH (TGCLOUD-1610). Reservation (computeLabelLayout) used
// 1.35 while the renderer stepped its multi-row blocks — external IDs,
// diagnoses, phenotypes, genotypes — by 1.4, so an N-row block drifted
// N * 0.05 * fontSize BELOW its reserved slot and ate into the block under it.
// Both now read this constant, so a row lands exactly where space was kept for
// it. (1.35 rather than 1.4: matching upward would have inflated every reserved
// block, and with it every auto LEVEL_HEIGHT, for no visual gain.)
export const LABEL_ROW_PITCH = 1.35;

// Approx number of characters that fit on one label line at a GIVEN node gap /
// symbol size / font — config-free so auto-spacing can ask "how many chars fit
// if the gap were X?" without mutating the live config (it computes a gap and
// must wrap at that candidate gap, not the current one).
export function labelMaxCharsAt(nodeGapPx, symbolSize, fontSize) {
  const sym = Number(symbolSize) || 45;
  const gap = Number(nodeGapPx) || 800;
  const fs = Number(fontSize) || 16;
  const avail = Math.max(gap - sym, fs); // never below ~one glyph
  return Math.max(1, Math.floor(avail / (fs * AVG_GLYPH_W)));
}

// Approx number of characters that fit on one label line at the current font,
// within the gap between a node's column and its neighbour's.
export function labelMaxChars(options) {
  return labelMaxCharsAt(
    config.NODE_GAP,
    config.SYMBOL_SIZE,
    options?.font_size
  );
}

// Greedy word-wrap `text` to lines of at most `maxChars` characters. A single
// token longer than a line is hard-broken so it can never overflow. Returns []
// for empty input.
export function wrapLabelText(text, maxChars) {
  const str = String(text ?? '').trim();
  if (!str) return [];
  const max = Math.max(1, Math.floor(maxChars) || 1);
  const lines = [];
  let line = '';
  for (let word of str.split(/\s+/)) {
    // Hard-break a token that can't fit on a line by itself.
    while (word.length > max) {
      if (line) {
        lines.push(line);
        line = '';
      }
      lines.push(word.slice(0, max));
      word = word.slice(max);
    }
    if (!word) continue;
    if (!line) line = word;
    else if (line.length + 1 + word.length <= max) line += ' ' + word;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// Normalise a person's field to a clean array of non-empty item strings.
export function labelBlockItems(d, field) {
  const v = d?.[field];
  if (v == null) return [];
  const arr = Array.isArray(v) ? v : [v];
  return arr.map((x) => String(x ?? '').trim()).filter(Boolean);
}

// The full ordered list of rendered lines for a label block: the (wrapped)
// header followed by each (wrapped) item. getLabelPos reserves
// `lines.length` rows and the renderer draws exactly these lines.
export function buildLabelBlockLines(headerText, items, maxChars) {
  const lines = [];
  if (headerText) lines.push(...wrapLabelText(headerText, maxChars));
  for (const item of items) lines.push(...wrapLabelText(item, maxChars));
  return lines;
}

// Vertical clearance (px below the node centre) the FIRST label must start at so
// it never collides with a fertility / infertility mark — or 0 when the person
// has none. Two mark shapes, both keyed off SYMBOL_SIZE so they scale with zoom:
//   • a SPOUSELESS person's own mark hangs straight below the node — fertile bar
//     at 0.85*sym, infertile lower bar at 1.05*sym (Canvas single-node render);
//   • a COUPLE's mark hangs from the relationship line at the union midpoint and
//     drops further — fertile bar at 1.4*sym, infertile lower bar at 1.65*sym
//     (Canvas `couple-fertility` path: drop = 1.4*sym, gap = 0.25*sym).
// A tightly-spaced couple puts that mark right where a partner's first label
// (e.g. a gender-identity acronym) sits, so the baseline must clear the DEEPEST
// applicable bar by a text ascent (~0.8em) plus a small gap. Pure + d3-free so
// getLabelPos can reserve exactly what the Canvas renderer will draw.
export function fertilityLabelClearance(d, sym, fs) {
  let markBottom = 0;

  const spouseless = !d?.spouseIds || d.spouseIds.length === 0;
  if (d?.fertility && spouseless) {
    markBottom = (d.fertility === 'infertile' ? 1.05 : 0.85) * sym;
  }

  const coupleFerts = d?.spouseRelationships
    ? Object.values(d.spouseRelationships)
        .map((r) => r && r.fertility)
        .filter(Boolean)
    : [];
  if (coupleFerts.length) {
    const coupleBottom = (coupleFerts.includes('infertile') ? 1.65 : 1.4) * sym;
    markBottom = Math.max(markBottom, coupleBottom);
  }

  if (!markBottom) return 0;
  const ascent = fs * 0.8; // text rises ~0.8em above its baseline
  const gap = sym * 0.2;
  return markBottom + ascent + gap;
}

// Compose each test record into styled SEGMENTS for its main line plus its
// comment. Each segment is { text, key } where `key` is the label toggle that
// styles it — so Test Name / Genes Tested / Test Result / Variants can be
// formatted INDEPENDENTLY; literal separators carry key:null (unstyled). The
// main line reads "<name> (<genes>, <result>, <variants>)" — bracket dropped
// when no inner segment shows, inner segments shown alone (no empty bracket)
// when the name toggle is off. `comment` is the test's own note (gated by the
// Test Comments toggle), rendered italic on its own line. Multiple genes/
// variants are comma-joined; a variant is the three HGVS fields pre-joined with
// " - " by the backend.
export function buildTestSegments(d, ls) {
  const records = Array.isArray(d?.testRecords) ? d.testRecords : [];
  // The single place per-test row order is decided (TGCLOUD-1663). buildTestItems
  // and buildTestBlockLines both route through here, and so do the renderer, the
  // row reservation and auto-spacing's measurement — so sorting here means all
  // of them see the same sequence and cannot drift.
  return sortLabelValues('tests', records, ls)
    .map((t) => {
      const inner = [];
      if (ls?.genesTested && t.genes && t.genes.length) {
        inner.push({ text: t.genes.join(', '), key: 'genesTested' });
      }
      if (ls?.testResults && t.result) {
        inner.push({ text: t.result, key: 'testResults' });
      }
      if (ls?.variants && t.variants && t.variants.length) {
        inner.push({ text: t.variants.join(', '), key: 'variants' });
      }
      const name = ls?.tests && t.name ? String(t.name).trim() : '';
      const segments = [];
      if (name) {
        segments.push({ text: name, key: 'tests' });
        if (inner.length) {
          segments.push({ text: ' (', key: null });
          inner.forEach((seg, i) => {
            if (i) segments.push({ text: ', ', key: null });
            segments.push(seg);
          });
          segments.push({ text: ')', key: null });
        }
      } else {
        inner.forEach((seg, i) => {
          if (i) segments.push({ text: ', ', key: null });
          segments.push(seg);
        });
      }
      const comment =
        ls?.pedigreeText && t.comment ? String(t.comment).trim() : '';
      return { segments, comment };
    })
    .filter((it) => it.segments.length || it.comment);
}

// Flat { main, comment } view (segments concatenated) — kept for callers/tests
// that want the composed string rather than styled runs.
export function buildTestItems(d, ls) {
  return buildTestSegments(d, ls).map((it) => ({
    main: it.segments
      .map((s) => s.text)
      .join('')
      .trim(),
    comment: it.comment
  }));
}

// One genotype row as independently-styleable runs — "BRCA1 (NCBIGene:672) -
// Heterozygote". Symbol / ID / carrier status are independent toggles: the
// " - " separator only appears when something precedes the status, and the
// status shows alone when both other toggles are off.
//
// SINGLE SOURCE OF TRUTH for the row (TGCLOUD-1610). The renderer used to build
// these runs inline while auto-spacing measured symbol + (id) only and
// getLabelPos gated the row on those same two toggles — so the carrier status
// was drawn but never measured (clipped labels) and a carrier-status-ONLY
// configuration reserved no vertical space at all, painting the row at y=0 on
// top of the symbol. Renderer, width measurement and height reservation now all
// read this one function, so they cannot drift again.
//
// A value we cannot interpret still renders verbatim, but derives no +/- marker
// — a gap that is otherwise invisible, because the label looks perfectly
// correct. Italic locates it on the chart; the family-card notice says what the
// italics mean (TGCLOUD-1647).
//
// Note: a diagnoses-sourced gene row (collectGenotypeSource) carries no
// carrierStatus, so it never produces a status run — deliberate, unchanged.
export function buildGenotypeRuns(g, ls) {
  if (!g) return [];
  const parts = getGenotypeDisplayParts(g);
  const symbol = ls?.genotypesName && parts.symbol ? parts.symbol : '';
  const idPart = ls?.genotypesID ? `(${g.id})` : '';
  const runs = [];
  if (symbol) runs.push({ text: symbol, key: 'genotypesName' });
  if (symbol && idPart) runs.push({ text: ' ', key: null });
  if (idPart) runs.push({ text: idPart, key: 'genotypesID' });
  if (ls?.genotypeCarrierStatus) {
    const carrierLabel = getCarrierStatusLabel(g.carrierStatus);
    if (carrierLabel) {
      if (runs.length) runs.push({ text: ' - ', key: null });
      runs.push({
        text: carrierLabel,
        key: 'genotypeCarrierStatus',
        italic: isUnrecognisedCarrierStatus(g.carrierStatus)
      });
    }
  }
  return runs;
}

// The composed text of a genotype row — what auto-spacing measures, so the
// measured string is character-for-character what the renderer draws.
export function genotypeRowText(g, ls) {
  return buildGenotypeRuns(g, ls)
    .map((r) => r.text)
    .join('');
}

// Estimated rendered line count for a free-text Person Note. The renderer
// pixel-wraps the note into N tspans while reservation used to allow exactly
// ONE row, so a multi-line note under-reported the node's block height. That
// slack was hidden by the old global LEVEL_HEIGHT; per-generation spacing
// (TGCLOUD-1610) removes the slack, so the estimate has to be honest.
//
// Uses the same conservative char estimate as every other block — AVG_GLYPH_W
// is deliberately a touch high, so this over-counts rather than under-counts
// against the pixel wrap. Explicit newlines start a new row (blank lines
// included), matching the renderer's per-source-line wrap.
export function estimateNoteLines(note, maxChars) {
  const str = String(note ?? '');
  if (!str.trim()) return 0;
  return str
    .split(/\r?\n/)
    .reduce(
      (n, line) => n + Math.max(1, wrapLabelText(line, maxChars).length),
      0
    );
}

// Run-aware word wrap: wrap a list of { text, key } segments into lines of
// { runs:[{text,key}], italic:false } at most `maxChars` wide, breaking at
// spaces (hard-breaking an over-long word) while preserving each character's
// styling key. Shared by reservation (line count) and rendering so they agree.
export function wrapSegments(segments, maxChars) {
  const max = Math.max(1, Math.floor(maxChars) || 1);
  let S = '';
  const keyAt = [];
  for (const seg of segments) {
    const text = String(seg?.text ?? '');
    for (const ch of text) {
      S += ch;
      keyAt.push(seg?.key ?? null);
    }
  }
  const lines = [];
  const pushRange = (a, b) => {
    while (a < b && S[a] === ' ') a++;
    while (b > a && S[b - 1] === ' ') b--;
    if (b <= a) return;
    const runs = [];
    let cs = a;
    let ck = keyAt[a];
    for (let j = a + 1; j < b; j++) {
      if (keyAt[j] !== ck) {
        runs.push({ text: S.slice(cs, j), key: ck });
        cs = j;
        ck = keyAt[j];
      }
    }
    runs.push({ text: S.slice(cs, b), key: ck });
    lines.push({ runs, italic: false });
  };
  let start = 0;
  let lastSpace = -1;
  let i = 0;
  while (i < S.length) {
    if (i - start >= max && i > start) {
      if (lastSpace > start) {
        pushRange(start, lastSpace);
        start = lastSpace + 1;
      } else {
        pushRange(start, i);
        start = i;
      }
      lastSpace = -1;
    }
    if (S[i] === ' ') lastSpace = i;
    i++;
  }
  if (start < S.length) pushRange(start, S.length);
  return lines;
}

// Wrapped, header-prefixed lines for the composed Tests block. Returns
// { runs:[{text,key}], italic } objects: the "Tests:" header is unstyled; each
// test's main wraps into per-field styled runs; the test's comment follows on
// its own italic line (keyed `pedigreeText` so the Test Comments toggle can also
// bold it). Empty when no test contributes anything. Used for BOTH layout
// reservation (line count) and rendering, so they stay in lockstep.
export function buildTestBlockLines(d, ls, maxChars) {
  const items = buildTestSegments(d, ls);
  if (!items.length) return [];
  const lines = [];
  for (const h of wrapLabelText('Tests:', maxChars)) {
    lines.push({ runs: [{ text: h, key: null }], italic: false });
  }
  for (const it of items) {
    if (it.segments.length) {
      lines.push(...wrapSegments(it.segments, maxChars));
    }
    for (const w of wrapLabelText(it.comment, maxChars)) {
      lines.push({ runs: [{ text: w, key: 'pedigreeText' }], italic: true });
    }
  }
  return lines;
}

// Whether a stacked block should render: a custom `enabled(ls)` predicate when
// present (the composed Tests block is on if ANY of its child toggles is set),
// otherwise the single `key` toggle.
export function blockEnabled(blk, ls) {
  return blk.enabled ? blk.enabled(ls) : !!ls?.[blk.key];
}

// A block's wrapped lines as { runs:[{text,key}], italic } objects — the single
// shape both getLabelPos (reservation, uses .length) and Canvas (render)
// consume, so their line counts and the vertical layout stay in lockstep. A
// block with a custom `buildLines` (the composed Tests block) controls its own
// runs/italics; otherwise it is the plain "<header> + one item per field entry"
// stack, all upright, every line a single run styled by the block's own key.
export function blockLines(blk, d, ls, maxChars) {
  if (!blockEnabled(blk, ls)) return [];
  if (blk.buildLines) return blk.buildLines(d, ls, maxChars);
  const items = labelBlockItems(d, blk.field);
  if (!items.length) return [];
  return buildLabelBlockLines(blk.header, items, maxChars).map((text) => ({
    runs: [{ text, key: blk.key }],
    italic: false
  }));
}

// Single source of truth for the header + data of every label that renders as a
// stacked header + item list. Shared by getLabelPos and Canvas. Most blocks map
// a single toggle `key` to a member `field`; the Tests block is composed from
// several child toggles (including the italic Test Comment) via `enabled` +
// `buildLines`.
export const STACKED_LABEL_BLOCKS = [
  {
    key: 'consultationDate',
    header: 'Consultation Date:',
    field: 'consultationDate',
    yKey: 'consultationDate_y'
  },
  {
    key: 'confirmedDiagnosis',
    header: 'Diagnosis:',
    field: 'confirmedDiagnosis',
    yKey: 'confirmedDiagnosis_y'
  },
  {
    key: 'tests',
    header: 'Tests:',
    yKey: 'test_y',
    enabled: (ls) =>
      !!(
        ls?.tests ||
        ls?.genesTested ||
        ls?.variants ||
        ls?.testResults ||
        ls?.pedigreeText
      ),
    buildLines: buildTestBlockLines
  }
];
